import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

// SSR/prerender-safe layout effect.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Tracks window scroll direction with an accumulator + cooldown,
 * mirroring useScrollDirection's behaviour but bound to window scroll.
 * Returns true when the sticky chrome should hide (scrolling down past
 * the threshold). Always returns false within ~8px of the top, and for
 * users who prefer reduced motion.
 */
export function useWindowScrollDirection(threshold = 24, resetKey?: unknown): boolean {
  const [hidden, setHidden] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const hiddenRef = useRef(false);
  // Sentinel: -1 means "not yet initialised". The first scroll event
  // adopts the current scroll position without flipping `hidden`, so we
  // never need to read window.scrollY at mount / route change (which
  // would force a synchronous layout on first paint).
  const lastY = useRef(-1);
  const accDelta = useRef(0);
  const cooldown = useRef(false);
  const cooldownTimer = useRef<ReturnType<typeof setTimeout>>();
  // Timestamp (performance.now) until which scroll events are absorbed
  // without accumulating. Covers iOS Safari URL-bar settle after route
  // changes that would otherwise re-hide the chrome.
  const settlingUntil = useRef(0);

  useEffect(() => { hiddenRef.current = hidden; }, [hidden]);

  // Force-reset hide state on route changes (or any caller-controlled key).
  // Must run BEFORE paint so the new route never renders with the stale
  // -translate-y-full transform (e.g. mobile footer link → Blog).
  useIsomorphicLayoutEffect(() => {
    if (resetKey === undefined) return;
    accDelta.current = 0;
    // Reset the sentinel so the first scroll after the route change re-adopts
    // the current Y without reading layout here.
    lastY.current = -1;
    hiddenRef.current = false;
    setHidden(false);
    settlingUntil.current =
      (typeof performance !== 'undefined' ? performance.now() : Date.now()) + 350;
  }, [resetKey]);

  useEffect(() => {
    if (prefersReducedMotion) {
      if (hiddenRef.current) setHidden(false);
      return;
    }

    let rafPending = false;
    let latestY = 0;

    const processScroll = () => {
      rafPending = false;
      const currentY = latestY;

      // Lazy-init baseline from the first scroll event so we never read
      // layout outside of a scroll callback (the browser has already
      // computed scroll position by the time `scroll` fires).
      if (lastY.current === -1) {
        lastY.current = currentY;
        return;
      }

      // Settle window after a route change: absorb scroll noise from iOS
      // Safari URL-bar animation / scrollTo(0,0) without flipping hidden.
      const now =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      if (now < settlingUntil.current) {
        lastY.current = currentY;
        accDelta.current = 0;
        if (hiddenRef.current) setHidden(false);
        return;
      }

      const delta = currentY - lastY.current;
      lastY.current = currentY;

      if (currentY <= 8) {
        accDelta.current = 0;
        if (hiddenRef.current) setHidden(false);
        return;
      }

      if ((delta > 0 && accDelta.current < 0) || (delta < 0 && accDelta.current > 0)) {
        accDelta.current = delta;
      } else {
        accDelta.current += delta;
      }

      if (cooldown.current) return;

      if (accDelta.current > threshold && !hiddenRef.current) {
        setHidden(true);
        accDelta.current = 0;
        cooldown.current = true;
        clearTimeout(cooldownTimer.current);
        cooldownTimer.current = setTimeout(() => { cooldown.current = false; }, 150);
      } else if (accDelta.current < -threshold && hiddenRef.current) {
        setHidden(false);
        accDelta.current = 0;
        cooldown.current = true;
        clearTimeout(cooldownTimer.current);
        cooldownTimer.current = setTimeout(() => { cooldown.current = false; }, 150);
      }
    };

    const onScroll = () => {
      latestY = window.scrollY;
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(processScroll);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(cooldownTimer.current);
    };
  }, [threshold, prefersReducedMotion]);

  return hidden;
}